import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { apiFetch, apiPost } from "../api/client.js";

export type Role = "admin" | "customer";

export interface Session {
  role: Role;
  username: string;
  display_name?: string;
  customer_id?: number;
  email?: string;
}

export type AuthStatus = "loading" | "authenticated" | "unauthenticated";

interface AuthContextValue {
  status: AuthStatus;
  session: Session | null;
  login: (username: string, password: string) => Promise<Role>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>("loading");
  const [session, setSession] = useState<Session | null>(null);

  useEffect(() => {
    let cancelled = false;
    apiFetch<Session>("/api/auth/me")
      .then((s) => {
        if (!cancelled) {
          setStatus("authenticated");
          setSession(s);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStatus("unauthenticated");
          setSession(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback(async (username: string, password: string): Promise<Role> => {
    await apiPost("/api/auth/login", { username, password });
    const s = await apiFetch<Session>("/api/auth/me");
    setStatus("authenticated");
    setSession(s);
    return s.role;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    await apiPost("/api/auth/logout").catch(() => {});
    setStatus("unauthenticated");
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ status, session, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within an AuthProvider");
  return ctx;
}
